'use client'

import { useEffect, useState } from 'react'

import { cx } from '@/lib/utils'

// The hero video is 3.5 MB and the LCP element is the H1 next to it, not the
// video itself. Mounting the <video> tag on first render makes the browser
// reserve bandwidth and connections for it during the LCP window. We delay
// the mount until after first paint so the H1 paints without competing for
// network resources. The dark gradient placeholder fills the hero area
// immediately so the layout doesn't shift when the video swaps in.
export const HeroVideoBackground = ({ fallbackText }: { fallbackText: string }) => {
  const [showVideo, setShowVideo] = useState(false)

  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void }
    const start = () => setShowVideo(true)
    if (w.requestIdleCallback) {
      w.requestIdleCallback(start, { timeout: 2000 })
    } else {
      const id = window.setTimeout(start, 600)
      return () => window.clearTimeout(id)
    }
  }, [])

  return (
    <div
      className={cx(
        'absolute top-1/2 left-1/2 h-full w-full -translate-x-1/2 -translate-y-1/2 overflow-hidden bg-cover bg-center',
        'bg-[#070d19]'
      )}
    >
      {showVideo && (
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
          poster="/images/design/ht-intro-poster.jpg"
          className="h-full w-full object-cover"
        >
          <source src="https://d26t98c74zewbt.cloudfront.net/ht-intro.mp4" type="video/mp4" />
          <track kind="captions" srcLang="en" src="/captions/empty.vtt" default />
          {fallbackText}
        </video>
      )}
      <div
        className={cx(
          'absolute inset-0 h-full w-full bg-no-repeat',
          'bg-[linear-gradient(0deg,rgba(6,12,23,0)_55%,#070d19_100%)] bg-size-[367.714%_100%] bg-position-[-652.931px_0px]',
          'lg:bg-[linear-gradient(118deg,#0b1528_24.08%,rgba(20,37,70,0)_38.44%)] lg:bg-size-[151.86%_142.962%] lg:bg-position-[0px_0px] lg:opacity-60'
        )}
      />
    </div>
  )
}
